import { OrderActions } from "../enums";

/**
 * Part conversion commands used by the order footer.
 *
 * Temporary two-call rule (until the backend stamps part conversion times
 * itself): the garaaz_transition call is best effort and never blocks the
 * meta-info call, because meta-info is what fires the Estimate Ready push and
 * the IAM part confirmation. A rejected transition (enquiry started before the
 * new states existed, on hold, already moved, …) keeps today's behaviour.
 */
export type PartConversionServices = {
  checkNextStateOfGaraazOrders: (
    orderId: string,
    action: string,
  ) => Promise<any>;
  updateOrderMetaInfo: (orderId: string, payload: any) => Promise<any>;
};

export type PartConversionDeps = {
  services: PartConversionServices;
  onTransitionError?: (action: string, error: unknown) => void;
};

async function tryTransition(
  orderId: string,
  action: OrderActions,
  deps: PartConversionDeps,
): Promise<boolean> {
  try {
    await deps.services.checkNextStateOfGaraazOrders(orderId, action);
    return true;
  } catch (error) {
    deps.onTransitionError?.(action, error);
    return false;
  }
}

export async function startPartConversion(
  orderId: string,
  time: string,
  deps: PartConversionDeps,
): Promise<void> {
  await tryTransition(orderId, OrderActions.START_PART_CONVERSION, deps);
  await deps.services.updateOrderMetaInfo(orderId, {
    partConversionStartTime: time,
  });
}

export async function completePartConversion(
  orderId: string,
  time: string,
  deps: PartConversionDeps,
): Promise<void> {
  await tryTransition(orderId, OrderActions.COMPLETE_PART_CONVERSION, deps);
  await deps.services.updateOrderMetaInfo(orderId, {
    partConversionEndTime: time,
  });
}

export async function markNoPartConversionNeeded(
  orderId: string,
  time: string,
  deps: PartConversionDeps,
): Promise<void> {
  const started = await tryTransition(
    orderId,
    OrderActions.START_PART_CONVERSION,
    deps,
  );
  if (started) {
    await tryTransition(orderId, OrderActions.COMPLETE_PART_CONVERSION, deps);
  }
  await deps.services.updateOrderMetaInfo(orderId, {
    partConversionStartTime: time,
    partConversionEndTime: time,
  });
}
